import { useState, useEffect, useCallback } from "react";

const ACTIVE_TIMEOUT = 30 * 60 * 1000; // 30 min (tab visible)
const HIDDEN_TIMEOUT = 5 * 60 * 1000;  // 5 min (tab hidden)
const STORAGE_KEY = "user";

export default function useSession() {
  const [user, setUser] = useState(null);
  const [isVisible, setIsVisible] = useState(
    document.visibilityState === "visible"
  );

  // ---- helpers ----

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const getTimeout = useCallback(() => {
    return isVisible ? ACTIVE_TIMEOUT : HIDDEN_TIMEOUT;
  }, [isVisible]);

  const isExpired = useCallback(
    (session) => {
      return Date.now() - session.lastActivityAt > getTimeout();
    },
    [getTimeout]
  );

  const updateActivity = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;

      const updated = {
        ...prev,
        lastActivityAt: Date.now(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ---- restore session on load ----
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;

    const session = JSON.parse(stored);

    if (isExpired(session)) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      setUser(session);
    }
  }, [isExpired]);

  // ---- visibility handling ----
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === "visible";
      setIsVisible(visible);

      if (visible && user && isExpired(user)) {
        logout();
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () =>
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
  }, [user, isExpired, logout]);

  // ---- auto-logout timer ----
  useEffect(() => {
    if (!user) return;

    const remainingTime =
      getTimeout() - (Date.now() - user.lastActivityAt);

    if (remainingTime <= 0) {
      logout();
      return;
    }

    const timer = setTimeout(logout, remainingTime);
    return () => clearTimeout(timer);
  }, [user, getTimeout, logout]);

  // ---- activity listeners ----
  useEffect(() => {
    if (!user || !isVisible) return;

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) =>
      window.addEventListener(event, updateActivity)
    );

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, updateActivity)
      );
    };
  }, [user, isVisible, updateActivity]);

  // ---- login ----
  const login = useCallback((userData) => {
    const session = {
      ...userData,
      lastActivityAt: Date.now(),
    };

    setUser(session);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }, []);

  return { user, login, logout };
}
